/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { IDToken, OktaAuth } from '@okta/okta-auth-js';
import { captureException as sentryCaptureException } from '@sentry/react';
import { setUser as sentrySetUser } from '@sentry/browser';

import { snackNotifications } from '../notification/SnackBarUtils';
import { PEARL_API_URL } from './api_config';
import { openLoginErrorModal, PearlUser, setPearlUserInfo } from '../Login/userSlice';
import { setApiVersion } from '../ui/uiSlice';
import { analyticsInitialize } from '../analytics_utils';
import { deleteSmartFhirCookieData } from '../Epic/smartFhirUtils';
import { deleteShareVueCookieData } from '../ShareVue/ShareVueUtils';

// Extend AxiosRequestConfig to include custom property 'skipInterceptor'
export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  skipInterceptor?: boolean;
}

let token: string | undefined;
let appTitle = document.title.includes('Creator') ? 'creator' : 'consumer';
const pearlApi = axios.create({
  baseURL: PEARL_API_URL,
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
    'x-pearl-app': appTitle,
  },
  // withCredentials: true, // Needed to save the returned cookie, but we set this for /auth/okta only
  transformRequest: [
    function (data, headers) {
      if (token) {
        // @ts-ignore  - It think headers can be undefined.  Not sure why, but throw an exception if so
        headers['Authorization'] = authToken();
      }
      return data;
    },
    // @ts-ignore
    ...axios.defaults.transformRequest,
  ],
});

export function clearAuthToken() {
  token = undefined;
  sentrySetUser({});
}

export function setAuthToken(token_?: string) {
  token = token_;
}

export function authToken() {
  return `Bearer ${token}`;
}

let oktaAuth: OktaAuth | undefined;

export function setOktaAuth(oktaAuth_: OktaAuth) {
  oktaAuth = oktaAuth_;
}

export class AppAccessDeniedError extends Error {
  constructor(public readonly title: string, public readonly status: number = 401) {
    super(title);
    this.name = 'AppAccessDeniedError';
  }
}

/**
 * Use the token that we already have, and send it to the backend.
 * This isn't talking with okta, only our own backend.
 */
export async function fetchAuthOkta(): Promise<AxiosResponse<{ data: PearlUser }>> {
  if (!oktaAuth) {
    throw new Error('oktaAuth not set!');
  }
  const isAuthenticated = await oktaAuth.isAuthenticated();
  console.log('oktaAuth.isAuthenticated?', isAuthenticated);
  const idToken: IDToken = (await oktaAuth.tokenManager.get('idToken')) as IDToken;

  if (!idToken) {
    if (isAuthenticated) {
      snackNotifications.error('You have been logged out.  You will need to refresh the page');
      await oktaAuth.token.getWithRedirect({
        responseType: 'token',
      });
      // I don't think the following can ever be reached, since the above redirects
      console.log('Redirect completed!');
      throw new Error('Redirecting to refresh token');
    }
    throw new Error('Unauthorised');
  }
  try {
    return await pearlApi.post(
      '/auth/okta',
      { data: { idToken: idToken.idToken, sourceApp: 'WEB' } },
      { withCredentials: true }
    );
  } catch (err) {
    const e = err as AxiosError<any>;
    const status = e.response?.status ?? 0;
    const title = e.response?.data?.errors?.[0]?.title as string | undefined;
    console.log('fetchAuthOkta() error', status, title, e.response?.data);

    // Example payload:
    // { errors: [{ status: "401", title: "Your user can't access MedPearl in this environment." }] }
    if (status === 401 && title?.includes("can't access MedPearl")) {
      throw new AppAccessDeniedError(title, status);
    }

    // Other 401s = token/session problems; let the caller decide whether to re-login
    if (status === 401) {
      throw e;
    }

    // Pass through everything else
    throw e;
  }
}

/**
 * This function is used to determine whether the user is logged in through teams or not,
 *  and it's expected to use team context as a parameter.
 */
export async function fetchTeamsAuth(teamsAuth: string) {
  if (teamsAuth) {
    return pearlApi.post<{ data: PearlUser }>(
      '/auth/teams',
      { data: { idToken: teamsAuth } },
      { withCredentials: true }
    );
  } else {
    console.log('Unable to fetch teamsAuth!');
    throw new Error('Need to refresh');
  }
}
export async function fetchOtkaClientId(
  application: string,
  keys: string[]
): Promise<{ id: string; usedKey: number } | undefined> {
  for (let i = 0; i < keys.length; i++) {
    if (!keys[i]) continue;
    let reply = await pearlApi.get(`/auth/okta/clients?name=${application}&key=${keys[i]}`);

    if (reply.data.data.client_id) {
      return { id: reply.data.data.client_id, usedKey: i };
    }
  }
  return;
}

// Whether we are currently rerequesting the okta token
let isRefreshing = false;
const failedRequests: { resolve: (token: string | null) => void; reject: (error: any) => void }[] = [];
function processQueue(error: any, token: string | null = null) {
  failedRequests.forEach((prom) => (error ? prom.reject(error) : prom.resolve(token)));
  failedRequests.length = 0;
}

let apiVersion_: string | undefined;

function setApiVersion_(version: string | undefined) {
  if (version === apiVersion_ || !version) {
    return;
  }
  apiVersion_ = version;
  if ('store' in window) {
    window.store?.dispatch(setApiVersion(version));
  }
}

// Interceptor for 4xx responses
pearlApi.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    setApiVersion_(response.headers['x-pearl-api-version']);
    // Check for break-the-glass in the Category header field:
    // https://build.fhir.org/security-labels.html
    // Category: http://terminology.hl7.org/CodeSystem/v3-ActReason#BTG; scheme="http://hl7.org/fhir/tag/security"; label="break the glass"
    // NOTE: This has never been tested! I have no idea if this works - it was implemented purely based on reading the spec.
    if (
      response.headers['category']?.includes('v3-ActReason#BTG') ||
      response.headers['category']?.includes('break the glass')
    ) {
      snackNotifications.warning('This patient is marked as break-the-glass.  Currently not supported.');
      return Promise.reject('break-the-glass');
    }
    return response;
  },
  /** This catches errors.
   *  For example:
   *  data: {
   *    message: 'Network Error'
   *    name: 'Error',
   *    stacK 'Error: Network Error\n at a.export (http://....)
   *    config: {
   *      transitional: {...}
   *      transformRequest: [null, null],
   *      transformResponse: [null],
   *      timeout: 0,
   *      headers: { Authorization: "Bearer xyz" }
   *      baseURL: "https://foo/api/v1",
   *      method: "patch",
   *      url: "/pages/xyz",
   *      data: "....."
   *    }
   *  }
   *
   */
  async function (data: {
    response?: any;
    // I added a __isRetryRequest to keep track of whether we retried this request already (because we needed to login first)
    config: CustomAxiosRequestConfig & { __isRetryRequest: boolean };
    error?: any;
    message?: string;
  }) {
    // Capture errors - but ignore 403 locks.  Log everything else to sentry
    const { response, config, error, message } = data;
    const { method, baseURL, url } = config || {};
    // Check if the request has 'skipInterceptor' flag
    if (response?.config?.skipInterceptor) {
      return response;
    }
    // Ignore lock-denied errors
    if (response?.status === 403 && config?.url?.includes('/lock')) return response;
    if (response?.status === 404) return response; // Don't log 404 errors
    if (message === 'canceled' || message === 'request aborted') return; //Ignore calls cancelled by signal.abort() meaning they were simply no longer needed
    if (config?.url?.includes('/smart_fhir')) return response; // Ignore for now
    // Ignore algolia click event errors
    if (config?.url?.includes('/search/event')) {
      console.error('pearlApi.interceptors Error caught /search/event:', typeof data, JSON.stringify(data, null, 2));
      return response;
    }
    if (response?.status !== 401 && !config?.__isRetryRequest && response?.status !== 400) {
      // 400 is a validation warning - important since we need to re-auth, but not something to log in sentry
      // Don't log errors for 401 (unauthorized) because we will retry the request
      console.error('pearlApi.interceptors Error caught:', typeof data, JSON.stringify(data, null, 2));
      // Note that sentry has a 100KB limit, so we can't pass the full config, because the 'data' field might be too large
      sentryCaptureException({
        response,
        config: { method, baseURL, url },
        error: error?.error || error,
        message: error?.message || message,
      });
    }

    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // If this isn't an internal login error (/auth/okta), show a toast notification.
    // Otherwise, pass the error through to be handled by userSlice catch to display login error modal.
    if (config && config?.url !== '/auth/okta') {
      if (!response) {
        const errorStr = message || error;
        if (errorStr) {
          snackNotifications.error(`Failed to make this request ${error?.status || ''}: ${errorStr}`);
          console.error('Failed to make this request', errorStr, config.url, message, JSON.stringify(error));
        }
      } else {
        const responseErrors = Array.isArray(response?.data) ? response.data : response?.data?.errors;
        const err = responseErrors
          ? ': ' + responseErrors.map((error: any) => `${error?.status}: ${error?.title}`).join(', ')
          : '.';
        switch (response?.status) {
          case 302: // redirect
            break; // that's fine, just ignore
          case 401:
            // This means that our token expired
            if (!config.__isRetryRequest) {
              console.log('retrying..', config?.url, 'isRefreshing:', isRefreshing);
              if (isRefreshing) {
                try {
                  const token = await new Promise((resolve, reject) => {
                    failedRequests.push({ resolve, reject });
                  });
                  if (!config.headers) config.headers = {};
                  config.headers.Authorization = `Bearer ${token}`;
                  return pearlApi(config);
                } catch (e) {
                  snackNotifications.error(`Request failed: ${(e as Error).message || e}`);
                  return Promise.reject(e);
                }
              } else {
                isRefreshing = true;
                config.__isRetryRequest = true;
                return new Promise((resolve, reject) => {
                  console.log('fetchAuthOkta()');
                  deleteSmartFhirCookieData();
                  deleteShareVueCookieData();
                  fetchAuthOkta()
                    .then((user) => {
                      console.log('fetchAuthOkta() returned', user);
                      setAuthToken(user.data.data.accessToken);
                      if (!config.headers) config.headers = {};
                      config.headers.Authorization = `Bearer ${token}`;
                      isRefreshing = false;
                      processQueue(null, token);
                      setAuthToken(user.data.data.accessToken);
                      const pearlUserInfo = user.data.data as PearlUser;
                      window.store.dispatch(
                        setPearlUserInfo({ pearlUserInfo, fromSmartFhirCookie: false, fromShareVueCookie: false })
                      );
                      sentrySetUser({ id: pearlUserInfo?.userId, email: pearlUserInfo?.email });
                      analyticsInitialize(pearlUserInfo);
                      resolve(pearlApi(config));
                    })
                    .catch((err) => {
                      console.log('Refetching okta auth failed with', err);
                      processQueue(err, null);
                      snackNotifications.error(`Request failed: ${err.message}`);
                      isRefreshing = false;
                      const errors = [{ title: err.message ?? err.name ?? err.title ?? 'Unknown Error', detail: '' }];
                      if (err.response?.data?.errors) {
                        errors.push(
                          ...err.response?.data?.errors?.map((e: any) => ({ title: e.title, detail: e.detail }))
                        );
                      }
                      window.store.dispatch(
                        openLoginErrorModal({
                          errors,
                          errorModalOpen: true,
                          fromSmartFhirCookie: false,
                          fromShareVueCookie: false,
                        })
                      );
                      reject(err);
                    });
                });
              }
            }
            snackNotifications.error('You have been logged out.  Please refresh the page');
            break;
          case 403:
            if (!config?.url?.includes('/lock')) {
              // Surpress noisy lock messages
              snackNotifications.error("Sorry, you don't have access to this" + err);
            }
            break;
          case 409:
            snackNotifications.warning("We're refreshing some data for you. Please try again" + err);
            break;
          case 410:
            snackNotifications.error("Sorry, we couldn't find what you're looking for" + err);
            break;
          case 400 /* Validation warnings */:
            snackNotifications.warning('Warning' + err);
            break;
          case 404:
            // 404 means that the document was not found.  Don't show error here
            break;
          case 500: // internal server error
            if (config?.url?.includes('/vector_search/query')) {
              // Be silent about issues with vector search, and handle the errors in the component
              break;
            }
            if (
              config?.url?.includes('/documentVersions/autocomplete-rt') ||
              config?.url?.includes('/documentVersions/search')
            ) {
              // Be silent about issues with autocomplete - temporarily
              console.log('silent about autocomplete errors', config?.url, response);
              break;
            }
            console.log('Got error for url:', config?.url, response);
            snackNotifications.error("Sorry, we've encountered an unexpected error" + err);
            break;
          case 503: // service unavailable
            snackNotifications.error('Sorry, request took too long and timed out');
            break;
          default:
            snackNotifications.error("Sorry, we've encountered an unexpected error" + err);
            break;
        }
      }
    }
    return Promise.reject(data);
  }
);

export default pearlApi;
